import { supabase } from '@/integrations/supabase/client';

export interface S3Object {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  isFolder: boolean;
}

export interface VideoMetadata {
  creationDate: string | null;
  duration: number | null;
}

interface S3Response<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function callS3Function<T>(action: string, params: Record<string, unknown> = {}): Promise<S3Response<T>> {
  try {
    const { data, error } = await supabase.functions.invoke('s3-files', {
      body: { action, ...params }
    });

    if (error) {
      console.error(`[s3Files] ${action} error:`, error);
      return { success: false, error: error.message };
    }

    if (data?.error) {
      return { success: false, error: data.error };
    }

    return { success: true, data: data as T };
  } catch (err) {
    console.error(`[s3Files] ${action} exception:`, err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function listS3Objects(prefix: string = ''): Promise<S3Response<S3Object[]>> {
  return callS3Function<S3Object[]>('list', { prefix });
}

export async function getS3DownloadUrl(key: string, forceDownload: boolean = true): Promise<S3Response<{ url: string }>> {
  // forceDownload: true adds Content-Disposition: attachment header for proper file download
  return callS3Function<{ url: string }>('download', { key, forceDownload });
}

export async function getS3UploadUrl(key: string, contentType: string): Promise<S3Response<{ url: string; key: string }>> {
  return callS3Function<{ url: string; key: string }>('getUploadUrl', { key, contentType });
}

// Upload a file to S3 using a presigned URL
export async function uploadFileToS3(
  file: File,
  s3Key: string,
  onProgress?: (progress: number) => void
): Promise<S3Response<{ key: string }>> {
  try {
    // Get presigned upload URL
    const urlResult = await getS3UploadUrl(s3Key, file.type || 'application/octet-stream');
    if (!urlResult.success || !urlResult.data?.url) {
      return { success: false, error: urlResult.error || 'Failed to get upload URL' };
    }

    // Upload file using XMLHttpRequest for progress tracking
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true, data: { key: s3Key } });
        } else {
        const errorMsg = xhr.status === 0 
          ? 'Upload bloqué par CORS - vérifiez la configuration S3'
          : `Upload failed: ${xhr.status} ${xhr.statusText}`;
        resolve({ success: false, error: errorMsg });
      }
    });
    
    xhr.addEventListener('error', () => {
      // Status 0 typically means CORS blocked the request
      resolve({ success: false, error: 'Erreur réseau - probablement un problème CORS sur le bucket S3' });
      });
      
      xhr.open('PUT', urlResult.data.url);
      // Note: Don't set Content-Type header as it's not signed in the presigned URL
      // This avoids CORS preflight issues
      xhr.send(file);
    });
  } catch (err) {
    console.error('[s3Files] uploadFileToS3 exception:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// Note: moveS3Object, renameS3Object, createS3Folder, deleteS3Object have been removed.
// The virtual folder system (rushes_virtual_folders/files) handles organization via metadata,
// eliminating the need for expensive S3 operations.

// One-time migration: move all files from Gambetta/* to root and delete Gambetta folder
export async function migrateGambettaToRoot(): Promise<S3Response<{
  migrated: { oldKey: string; newKey: string }[];
  errors: { key: string; error: string }[];
  migratedCount: number;
  errorCount: number;
}>> {
  return callS3Function('migrateGambetta', {});
}

export interface FolderDownloadInfo {
  folderName: string;
  files: { key: string; name: string; url: string }[];
}

// Get presigned download URLs for all files in a folder
export async function getFolderDownloadUrls(folderKey: string): Promise<S3Response<FolderDownloadInfo>> {
  return callS3Function<FolderDownloadInfo>('downloadZip', { folderKey });
}

// Get video metadata (creation date, duration)
export async function getVideoMetadata(key: string): Promise<S3Response<VideoMetadata>> {
  return callS3Function<VideoMetadata>('metadata', { key });
}

// Get a streaming URL for video files (with CORS headers for client-side processing)
export async function getS3StreamUrl(key: string): Promise<string | null> {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) return null;
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const baseUrl = `${supabaseUrl}/functions/v1/s3-files`;
    
    // We need to make a POST request to get the stream
    // Return a blob URL by fetching the stream
    const response = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ action: 'stream', key }),
    });
    
    if (!response.ok) return null;
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error('[s3Files] getS3StreamUrl error:', err);
    return null;
  }
}

// Helper to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Helper to get file extension
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

// Helper to determine if file is a video
export function isVideoFile(filename: string): boolean {
  const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v', 'mxf', 'prores'];
  return videoExtensions.includes(getFileExtension(filename));
}

// Helper to determine if file is an image
export function isImageFile(filename: string): boolean {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'ico'];
  return imageExtensions.includes(getFileExtension(filename));
}

// Helper to determine if file is audio
export function isAudioFile(filename: string): boolean {
  const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'm4a', 'ogg', 'wma'];
  return audioExtensions.includes(getFileExtension(filename));
}
